// ============================================================================
// App shell: app bar, theme control, auth gating, and view routing. Keeps the
// presentation thin — all data access goes through the portable core (api.js).
// ============================================================================

import { el, mount, toast } from './dom.js';
import { getState, setState, subscribe } from './state/store.js';
import { getPreference, cyclePreference } from './theme.js';
import { isConfigured } from '@core/data/supabase.js';
import * as api from '@core/data/api.js';

import { renderAuth } from './views/auth.js';
import { renderDashboard } from './views/dashboard.js';
import { renderStudy } from './views/study.js';
import { renderBrowse } from './views/browse.js';
import { renderItemDetail } from './views/itemDetail.js';
import { renderLearn } from './views/learn.js';
import { renderCredits } from './views/credits.js';
import { renderPath } from './views/path.js';
import { renderLesson } from './views/lesson.js';
import { renderReading } from './views/reading.js';
import { renderDecks } from './views/decks.js';
import { renderSettings } from './views/settings.js';
import { renderAdmin } from './views/admin.js';

let root;

const THEME_LABEL = { light: '☀ Light', dark: '☾ Dark', system: '◐ System' };

function appbar() {
  const { session, profile } = getState();
  const themeBtn = el('button.btn.btn--ghost.theme-toggle', {
    title: 'Toggle light / dark / system',
    onclick: () => { cyclePreference(); render(); },
    text: THEME_LABEL[getPreference()],
  });

  const nav = [];
  if (session) {
    const route = getState().route || '#/';
    const active = (href) => href === '#/' ? (route === '#/' || route === '') : route.startsWith(href);
    const link = (href, label) => el(`button.btn.btn--ghost.navlink${active(href) ? '.navlink--active' : ''}`, {
      text: label, onclick: () => navigate(href),
    });
    nav.push(
      link('#/', 'Home'),
      link('#/path', 'Guided'),
      link('#/browse', 'Browse'),
      link('#/decks', 'Decks'),
      link('#/study', 'Practice'),
      link('#/learn', 'How it works'),
    );
    if (getState().isAdmin) {
      const pending = getState().pendingUserCount || 0;
      const adminLink = link('#/admin', 'Admin');
      if (pending > 0) {
        adminLink.appendChild(el('span.nav-badge', {
          text: String(pending), title: `${pending} user${pending === 1 ? '' : 's'} awaiting activation`,
        }));
      }
      nav.push(adminLink);
    }
  }

  const right = [themeBtn];
  if (session) {
    right.push(
      el('button.btn.btn--ghost', {
        text: '⚙ Settings', title: 'Settings', onclick: () => navigate('#/settings'),
      }),
      el('button.btn.btn--ghost', {
        text: 'Sign out',
        onclick: async () => { await api.signOut(); },
      })
    );
  }

  return el('header.appbar', {}, [
    el('button.appbar__brand.link-btn', { html: 'Korean <span class="han">漢字</span> Hanja', onclick: () => navigate('#/') }),
    ...nav,
    el('div.appbar__spacer'),
    ...right,
  ]);
}

function currentView() {
  const { session } = getState();
  if (!isConfigured) return renderSetup();
  if (!session) return renderAuth({ onDone: refreshSession });
  const route = getState().route;
  if (route.startsWith('#/study')) return renderStudy({ navigate });
  if (route.startsWith('#/path')) return renderPath({ navigate });
  if (route.startsWith('#/lesson/')) {
    const [, , lessonId] = route.split('/'); // #/lesson/<id>
    return renderLesson({ navigate, id: lessonId });
  }
  if (route.startsWith('#/browse')) return renderBrowse({ navigate });
  if (route.startsWith('#/decks')) return renderDecks({ navigate });
  if (route.startsWith('#/reading')) {
    const [, , eum] = route.split('/'); // #/reading or #/reading/<eum>
    return renderReading({ navigate, eum: eum ? decodeURIComponent(eum) : null });
  }
  if (route.startsWith('#/learn')) return renderLearn({ navigate });
  if (route.startsWith('#/settings')) return renderSettings({ navigate });
  if (route.startsWith('#/admin')) return renderAdmin({ navigate });
  if (route.startsWith('#/credits')) return renderCredits({ navigate });
  if (route.startsWith('#/item/')) {
    const [, , type, id] = route.split('/'); // #/item/<type>/<id>
    return renderItemDetail({ navigate, type, id });
  }
  return renderDashboard({ navigate });
}

function renderSetup() {
  return el('div.container', {}, [
    el('div.card.auth-wrap.stack', {}, [
      el('h1', { text: 'Setup needed' }),
      el('p.muted', {
        text:
          'The Supabase connection is not configured. Copy .env.example to .env ' +
          'and set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY, then restart the dev server.',
      }),
    ]),
  ]);
}

let booting = true;

function footer() {
  return el('footer.appfooter', {}, [
    el('span.faint', { text: 'Korean 漢字 Hanja · content from open data' }),
    el('button.link-btn', { text: 'Credits & sources', onclick: () => navigate('#/credits') }),
  ]);
}

function render() {
  try {
    mount(root, appbar(), booting ? bootingView() : currentView(), footer());
  } catch (e) {
    mount(root, appbar(), el('div.container', {}, [
      el('div.card.banner.banner--warn', { text: 'Render error: ' + (e?.message || e) }),
    ]), footer());
  }
}

function bootingView() {
  return el('div.container', {}, [el('div.card', { text: 'Loading…' })]);
}

function navigate(route) {
  if (location.hash !== route) location.hash = route;
  else { setState({ route }); render(); }
}

// Load profile/stats for a session and repaint. MUST NOT be called from inside
// the onAuthStateChange callback synchronously (see startApp) — doing so would
// deadlock supabase-js's auth lock.
async function applySession(session) {
  setState({ session });
  if (session) {
    try {
      const profile = await api.ensureProfile();
      const stats = await api.getMyStats().catch(() => null);
      const isAdmin = await api.isAdmin().catch(() => false);
      setState({ profile, stats, isAdmin });
      if (isAdmin) refreshPendingCount();
    } catch (e) {
      toast(e.message || 'Could not load your profile', 'error');
    }
  } else {
    setState({ profile: null, stats: null, isAdmin: false, pendingUserCount: 0 });
  }
  booting = false;
  render();
}

// Refresh the "users awaiting activation" count and repaint the nav badge.
// Exported so the admin Users screen can update it after (de)activating a user.
export async function refreshPendingCount() {
  if (!getState().isAdmin) return;
  const n = await api.adminPendingCount().catch(() => 0);
  setState({ pendingUserCount: n });
  render();
}

// Safe to call from ordinary event handlers (e.g. after sign-in), NOT from the
// auth-state-change callback.
async function refreshSession() {
  let session = null;
  try {
    session = await api.getSession();
  } catch (e) {
    toast('Auth error: ' + (e.message || e), 'error');
  }
  await applySession(session);
}

export function startApp(mountEl) {
  root = mountEl;

  window.addEventListener('hashchange', () => {
    setState({ route: location.hash || '#/' });
    render();
  });
  setState({ route: location.hash || '#/' });

  subscribe(() => {}); // reserved for future cross-view reactivity

  if (isConfigured) {
    render(); // paint a loading state immediately so the shell never blanks
    // Use the session handed to the callback and DEFER out of the auth lock to
    // avoid the supabase-js onAuthStateChange deadlock.
    api.onAuthChange((session) => setTimeout(() => applySession(session), 0));
    // Initial read is outside the auth callback, so it's safe to await here.
    refreshSession();
  } else {
    booting = false;
    render();
  }
}
